import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Hammer, Palette, ClipboardCheck, Camera, Home, Percent, ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { BRAND } from "../lib/brand";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCENARIOS = [
  {
    value: "own_land",
    title: "I already own land",
    desc: "Help me design, build, and operate it as a holiday rental.",
    icon: CheckCircle2,
  },
  {
    value: "buy_and_build",
    title: "I want to buy land + build",
    desc: "Find the right plot, then design and build the property end-to-end.",
    icon: MapPin,
  },
  {
    value: "exploring",
    title: "I'm just exploring",
    desc: "I want to understand how Zentry Build works before deciding.",
    icon: Home,
  },
];

export default function ZentryBuild() {
  const includes = [
    { icon: Home, t: "Land identification", d: "In the right micro-market with genuine demand and accessibility." },
    { icon: ClipboardCheck, t: "Legal verification", d: "Including Bhu Kanoon & CRZ compliance where applicable." },
    { icon: Palette, t: "Architecture briefed for Airbnb", d: "View orientation, room layout, outdoor spaces, photography potential." },
    { icon: Hammer, t: "Construction oversight", d: "Transparent progress updates from site." },
    { icon: Camera, t: "Interior + listing setup", d: "Design, photography, copywriting, and pricing strategy." },
    { icon: Percent, t: "Flat 10% consulting fee", d: "No vendor commissions. No hidden margins." },
  ];

  const [form, setForm] = useState({
    scenario: "",
    location_pref: "",
    budget: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.scenario || !form.name || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const notesParts = [
        `Zentry Build enquiry`,
        `Scenario: ${form.scenario}`,
        form.location_pref && `Preferred location: ${form.location_pref}`,
        form.budget && `Budget: ${form.budget}`,
        form.notes && `Notes: ${form.notes}`,
      ].filter(Boolean).join(" | ");

      await axios.post(`${API}/clarity-call/book`, {
        looking_for: "holiday_home",
        desired_outcome: "rental_income",
        budget_min: 7500000,
        budget_max: 50000000,
        loan_required: "maybe",
        timeline: "exploring",
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: notesParts,
      });
      setSubmitted(true);
      toast.success("Submitted — we'll be in touch within 24 hours.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="zentry-build-page">
      <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Zentry Build</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 max-w-4xl leading-[1.1]">
        A property designed for the Airbnb guest — not for a developer's margin.
      </h1>
      <p className="text-lg text-zinc-600 mt-8 max-w-3xl leading-relaxed">
        In many markets, the ready-to-buy inventory is either the wrong product, wrong design, or wrong price. The developers building it have never run an Airbnb. Zentry Build is the alternative — coordinated end to end.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mt-12">
        {BRAND.holidayImages.slice(0, 2).map((src, i) => (
          <div key={i} className="rounded-3xl overflow-hidden border border-zinc-200 aspect-[16/10]">
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>

      <h2 className="font-display text-4xl mt-20 max-w-2xl">What Zentry Build includes</h2>
      <div className="mt-10 grid md:grid-cols-3 gap-6">
        {includes.map((x, i) => (
          <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#1D4ED8] hover:-translate-y-1 transition-all">
            <x.icon className="h-6 w-6 text-[#1D4ED8]" />
            <div className="font-display text-2xl mt-4">{x.t}</div>
            <p className="text-zinc-600 mt-2 text-sm">{x.d}</p>
          </div>
        ))}
      </div>

      <div className="mt-20 bg-[#EFF6FF] border border-[#1D4ED8]/20 rounded-3xl p-10 md:p-12">
        <div className="font-display text-3xl max-w-2xl leading-snug">Why it saves money</div>
        <p className="text-zinc-700 mt-4 max-w-2xl">
          Buying from a developer means paying a 25–35% margin for a product designed without Airbnb understanding. Zentry Build's 10% fee gives you a purpose-built asset at 25–30% less.
        </p>
      </div>

      {/* SCENARIO + ENQUIRY FORM */}
      <section className="mt-20" data-testid="zentry-build-form">
        <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Start your build</div>
        <h2 className="font-display text-4xl sm:text-5xl mt-4 max-w-3xl tracking-tight leading-tight">
          Where are you in the journey?
        </h2>
        <p className="text-zinc-600 mt-4 max-w-2xl">Tell us your starting point — we'll customise the conversation accordingly.</p>

        {submitted ? (
          <div className="mt-10 bg-white border-2 border-[#1D4ED8] rounded-3xl p-10 md:p-12" data-testid="zentry-build-success">
            <div className="w-12 h-12 rounded-full bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div className="font-display text-3xl">Thanks {form.name.split(" ")[0]} — your enquiry is in.</div>
            <p className="text-zinc-600 mt-3 max-w-xl">We'll reach out within 24 hours at {form.email} to set up a Zentry Build consultation.</p>
            <Link to="/" className="btn-secondary mt-6 inline-flex">Back to Home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 bg-white border border-zinc-200 rounded-3xl p-8 md:p-10 space-y-8">
            <div>
              <Label className="text-sm text-zinc-700">Your scenario *</Label>
              <div className="mt-3 grid md:grid-cols-3 gap-3">
                {SCENARIOS.map((s) => {
                  const Ic = s.icon;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setForm({ ...form, scenario: s.value })}
                      className={`text-left p-5 rounded-2xl border-2 transition-all ${form.scenario === s.value ? "border-[#1D4ED8] bg-[#EFF6FF]" : "border-zinc-200 hover:border-zinc-400"}`}
                      data-testid={`scenario-${s.value}`}
                    >
                      <Ic className="h-5 w-5 text-[#1D4ED8]" />
                      <div className="font-display text-lg mt-3">{s.title}</div>
                      <p className="text-sm text-zinc-600 mt-1">{s.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-zinc-700">Preferred location (optional)</Label>
                <Input value={form.location_pref} onChange={(e) => setForm({ ...form, location_pref: e.target.value })} className="mt-2" placeholder="e.g. North Goa, Mussoorie" data-testid="zb-location" />
              </div>
              <div>
                <Label className="text-sm text-zinc-700">Approximate budget (optional)</Label>
                <Input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-2" placeholder="₹1–3 Cr" data-testid="zb-budget" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-zinc-700">Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="zb-name" />
              </div>
              <div>
                <Label className="text-sm text-zinc-700">Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" data-testid="zb-email" />
              </div>
              <div>
                <Label className="text-sm text-zinc-700">Phone *</Label>
                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" placeholder="+91..." data-testid="zb-phone" />
              </div>
            </div>

            <div>
              <Label className="text-sm text-zinc-700">Anything we should know?</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" placeholder="Site details, timeline, vision, anything specific..." data-testid="zb-notes" />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" disabled={submitting || !form.scenario} className="btn-primary disabled:opacity-50" data-testid="zb-submit">
                {submitting ? "Submitting..." : "Start the conversation"} <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/clarity-call" className="text-sm text-zinc-600 hover:text-[#1D4ED8] link-underline">Or book a Clarity Call instead</Link>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
